/**
 * EmployeeAvatar — the one visual identity for a Digital Employee (EMPLOYEE-IDENTITY-PERSONA-001).
 *
 * avatar_key is a short glyph (emoji) or nothing (falls back to the name's initials); avatar_color
 * is an allow-listed palette key mapped to FIXED Tailwind classes below — never an arbitrary CSS
 * value, never a URL. The same component is used on the Agents list, the Agent workspace, and
 * inside conversation bubbles, so an employee looks the same everywhere.
 */

const COLOR_CLASSES = {
  blue:   "bg-blue-100 text-blue-800 ring-blue-200",
  violet: "bg-violet-100 text-violet-800 ring-violet-200",
  green:  "bg-green-100 text-green-800 ring-green-200",
  amber:  "bg-amber-100 text-amber-800 ring-amber-200",
  rose:   "bg-rose-100 text-rose-800 ring-rose-200",
  teal:   "bg-teal-100 text-teal-800 ring-teal-200",
  slate:  "bg-slate-200 text-slate-800 ring-slate-300",
};

const SIZE_CLASSES = {
  sm: "w-7 h-7 text-sm",
  md: "w-10 h-10 text-lg",
  lg: "w-16 h-16 text-3xl",
};

export const STATUS_META = {
  available: { label: "זמין",   dot: "bg-green-500" },
  busy:      { label: "עסוק",   dot: "bg-amber-500" },
  away:      { label: "לא כאן", dot: "bg-gray-400" },
  offline:   { label: "מנותק",  dot: "bg-gray-300" },
};

export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

/**
 * The status a surface may SHOW for an employee, from the stored self-status and the enabled flag.
 *
 * A disabled employee is offline, whatever self-status is stored — the same rule the backend
 * applies everywhere else (employee_workspace.effective_status). `availability_status` defaults
 * to "available" at the model level, so a caller that reads that raw value would otherwise draw a
 * green "זמין" dot and title on an employee it is labelling "מושבת" one line away.
 *
 * Only an explicit `false` disables: a caller that omits `isEnabled` (or passes a null it could
 * not resolve) keeps today's exact rendering, so this cannot silently offline a whole surface.
 *
 * Exported because the avatar is not the only channel that shows presence: a page that also prints
 * the status as text next to the dot reads the rule from here rather than restating it.
 */
export function drawnStatus(status, isEnabled) {
  return isEnabled === false ? "offline" : status;
}

export default function EmployeeAvatar({ name, avatarKey, avatarColor, status, isEnabled, size = "md", showStatus = false }) {
  const color = COLOR_CLASSES[avatarColor] || COLOR_CLASSES.violet;
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const statusMeta = STATUS_META[drawnStatus(status, isEnabled)];
  return (
    <span className="relative inline-flex shrink-0" title={statusMeta ? `${name} — ${statusMeta.label}` : name}>
      <span
        aria-label={name}
        className={`inline-flex items-center justify-center rounded-full ring-2 font-semibold select-none ${color} ${sizeCls}`}
      >
        {avatarKey || initialsOf(name)}
      </span>
      {showStatus && statusMeta && (
        <span
          aria-label={statusMeta.label}
          className={`absolute -bottom-0.5 -left-0.5 w-3 h-3 rounded-full ring-2 ring-white ${statusMeta.dot}`}
        />
      )}
    </span>
  );
}
